import './GeoFence.scss';

import React, { Fragment, useEffect, useState } from 'react';

import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import MapIcon from '@mui/icons-material/Map';
import { Button, Tooltip } from '@mui/material';

import SelectCity from '../../components/SelectCity';
import SelectDistance from '../../components/SelectDistance';
import SelectLatitude from '../../components/SelectLatitude';
import SelectLongitude from '../../components/SelectLongitude';
import SelectStreet from '../../components/SelectStreet';
import SelectUSState from '../../components/SelectUSState';
import SelectZipCode from '../../components/SelectZipCode';
import { isNamedElementActive } from '../../lib/HTMLUtilities';
import zipCodeData from '../../lib/Zip2LatLong.json';
import APIService from '../../shared/api-service';
import IData from '../../shared/IData';
import IState from '../../shared/IState';

export interface IAddProps {
    setIsPaneOpen: Function;
    handleSave: Function;
    setShowCancelCreateAlert: Function;
    readOnly: boolean;
    data: IData | undefined;
}

export default function GeoFence(
    { data, readOnly, setError, setIsDirty,
        selectedStreet, setSelectedStreet,
        selectedCity, setSelectedCity,
        selectedUSState, setSelectedUSState,
        selectedZipCode, setSelectedZipCode,
        selectedLatitude, setSelectedLatitude,
        selectedLongitude, setSelectedLongitude,
        selectedDistance, setSelectedDistance,
        setHasEmptyRequiredFields,
        hasFetchGeoButton = true
    }:
        {
            data: IData | undefined, readOnly: boolean, setError: Function, setIsDirty: Function,
            selectedStreet: string, setSelectedStreet: Function,
            selectedCity: string, setSelectedCity: Function,
            selectedUSState: IState | undefined, setSelectedUSState: Function,
            selectedZipCode: string, setSelectedZipCode: Function,
            selectedLatitude: string | undefined, setSelectedLatitude: Function,
            selectedLongitude: string | undefined, setSelectedLongitude: Function
            selectedDistance: { value: string, zoom: string } | undefined, setSelectedDistance: Function,
            setHasEmptyRequiredFields: Function,
            hasFetchGeoButton: boolean
        }
) {
    const [zipCodeError, setZipCodeError] = useState<boolean>(false);
    const [longitudeError, setLongitudeError] = useState<boolean>(false);
    const [latitudeError, setLatitudeError] = useState<boolean>(false);
    const [allAddressFieldsRequired, setAllAddressFieldsRequired] = useState<boolean>(false);
    const [allLocationFieldsRequired, setAllLocationFieldsRequired] = useState<boolean>(false);
    const [onlyZipCodFieldHasData, setOnlyZipCodeFieldHasData] = useState<boolean>(false);
    const [showMap, setShowMap] = useState<boolean>(false);
    const [hasFullAddress, setHasFullAddress] = useState<boolean>(true);
    const [gettingCoordinates, setGettingCoordinates] = useState<boolean>(false);
    const [result, setResult] = useState<any>();
    const [shiftHeld, setShiftHeld] = useState<boolean>(false);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const [showResultsTooltip, setShowResultsTooltip] = useState<boolean>(false);

    function downHandler({ key }: { key: any }) {
        if (key === 'Shift') {
            if (!isNamedElementActive('ignore-shift-key-pub-sub')) {
                setShiftHeld(true);
            }
        }
    }

    function upHandler({ key }: { key: any }) {
        if (key === 'Shift') {
            if (!isNamedElementActive('ignore-shift-key-pub-sub')) {
                setShiftHeld(false);
            }
        }
    }

    useEffect(() => {
        window.addEventListener('keydown', downHandler);
        window.addEventListener('keyup', upHandler);
        return () => {
            window.removeEventListener('keydown', downHandler);
            window.removeEventListener('keyup', upHandler);
        };
    }, []);

    useEffect(() => {
        const err = latitudeError || longitudeError || zipCodeError;
        setError(err);
        if (err) {
            setResult(undefined);
        }
    }, [latitudeError, longitudeError, zipCodeError, setError])

    useEffect(() => {
        const _onlyZipCodeFieldHasData = (!!selectedZipCode) && (!selectedStreet) && (!selectedCity) && (!selectedUSState);
        setOnlyZipCodeFieldHasData(_onlyZipCodeFieldHasData)
        const atLeastOneAddressControlHasData = !!selectedStreet || !!selectedCity || !!selectedUSState;
        setAllAddressFieldsRequired(atLeastOneAddressControlHasData);
        const atLeastOneLocationControlHasData = !!selectedLatitude || !!selectedLongitude || !!selectedDistance;
        setAllLocationFieldsRequired(atLeastOneLocationControlHasData);
        const allAddressControlsHaveData = !!selectedStreet && !!selectedCity && !!selectedUSState && !!selectedZipCode;
        const allLocationControlsHaveData = !!selectedLatitude && !!selectedLongitude && !!selectedDistance;
        const hasEmptyRequiredFields = (atLeastOneAddressControlHasData && !allAddressControlsHaveData) ||
            ((atLeastOneAddressControlHasData || atLeastOneLocationControlHasData) && !allLocationControlsHaveData);
        setHasEmptyRequiredFields(hasEmptyRequiredFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStreet, selectedCity, selectedUSState, selectedZipCode, selectedLatitude, selectedLongitude, selectedDistance])

    useEffect(() => {
        setHasFullAddress((selectedStreet && selectedStreet.length > 0) &&
            (selectedCity && selectedCity.length > 0) &&
            (selectedUSState) &&
            (selectedZipCode && selectedZipCode.length > 0 && !zipCodeError) ? true : false);

    }, [selectedStreet, selectedCity, selectedUSState, selectedZipCode, zipCodeError])

    useEffect(() => {
        if (!hasFullAddress) {
            setResult(undefined);
        }
    }, [hasFullAddress])

    const handleGetCoordinates = () => {
        setGettingCoordinates(true);
        if (onlyZipCodFieldHasData) {
            const zipData = zipCodeData.zipcodeToLatLong.find((zip) => zip.z === selectedZipCode);
            if (zipData) {
                setResult(zipData);
                setSelectedLatitude(zipData.lat);
                setSelectedLongitude(zipData.lon);
            } else {
                setResult({msg: 'Zip code not found.', "latLongKey": "0.0,0.0"})
                setSelectedLatitude(0);
                setSelectedLongitude(0);
            }
            setGettingCoordinates(false);
        } else {
            const state: string = selectedUSState ? selectedUSState.abbreviation : '';
            console.log('Get Coordinates');
            console.log('selectedStreet: ', selectedStreet);
            console.log('selectedCity: ', selectedCity);
            console.log('selectedUSState: ', state);
            console.log('selectedZipCode :', selectedZipCode);

            const itemService = new APIService();
            itemService.retrieveLatitudeAndLongitudeFromAddress(
                selectedStreet, selectedCity, state, selectedZipCode
            ).then((result: any) => {
                console.log('result: ', result);
                setResult(result);
                setSelectedLatitude(result.latitude);
                setSelectedLongitude(result.longitude);
                setGettingCoordinates(false);
            }).catch((error: any) => {
                console.log('error: ', error);
                setResult(error);
                setGettingCoordinates(false);
            })
        }
    }

    const PrettyPrintJson = (data: any) => (<div><pre>{
        JSON.stringify(data, null, 2)}</pre></div>);

    const disableButton = zipCodeError || (!hasFullAddress && !onlyZipCodFieldHasData) || gettingCoordinates;
    const mapSrc = `https://maps.google.com/maps?q=${selectedLatitude},${selectedLongitude}&z=${selectedDistance?.zoom}&ie=UTF8&iwloc=&output=embed`;
    return (
        <Fragment>
            <div className={'box'}>
                <div className={'box-label'}>Geo-Fencing</div>
                <SelectStreet initialValue={data?.geoFenceStreetAddress}
                    selectedStreet={selectedStreet}
                    setSelectedStreet={setSelectedStreet}
                    onChangeStreet={() => setIsDirty(true)}
                    readOnly={readOnly}
                    required={allAddressFieldsRequired && !onlyZipCodFieldHasData} />
                <SelectCity initialValue={data?.geoFenceCity}
                    selectedCity={selectedCity}
                    setSelectedCity={setSelectedCity}
                    onChangeCity={() => setIsDirty(true)}
                    readOnly={readOnly}
                    required={allAddressFieldsRequired && !onlyZipCodFieldHasData} />
                <SelectUSState initialValue={data?.geoFenceState}
                    selectedUSState={selectedUSState}
                    setSelectedUSState={setSelectedUSState}
                    onChangeUSState={() => setIsDirty(true)}
                    readOnly={readOnly}
                    required={allAddressFieldsRequired && !onlyZipCodFieldHasData} />
                <SelectZipCode initialValue={data?.geoFenceZipCode}
                    selectedZipCode={selectedZipCode}
                    setSelectedZipCode={setSelectedZipCode}
                    onChangeZipCode={() => setIsDirty(true)}
                    zipCodeError={zipCodeError}
                    setZipCodeError={setZipCodeError}
                    readOnly={readOnly}
                    required={allAddressFieldsRequired || !setOnlyZipCodeFieldHasData} />

                {hasFetchGeoButton &&
                    <div className={'button'}>
                        <Tooltip
                            open={!shiftHeld && showTooltip}
                            onClose={() => { setShowTooltip(false) }}
                            onMouseEnter={() => { setShowTooltip(true) }}
                            onMouseLeave={() => { setShowTooltip(false) }}
                            arrow title={! onlyZipCodFieldHasData ? "Convert Street, City, State, Zip to Latitude and Longitude" : "Convert Zip Code to Latitude and Longitude"}
                        >
                            <span>
                                <Button disabled={disableButton} variant="outlined" startIcon={
                                    <div className={'wrapper-location-icon'}>
                                        {!!result &&
                                            <Tooltip
                                                open={shiftHeld && showResultsTooltip}
                                                onClose={() => { setShowResultsTooltip(false) }}
                                                onMouseEnter={() => { setShowResultsTooltip(true) }}
                                                onMouseLeave={() => { setShowResultsTooltip(false) }}
                                                arrow title={PrettyPrintJson(result)} placement='top' >
                                                <GpsFixedIcon className={disableButton ? '' : gettingCoordinates ? 'location-icon-animated' : result.latLongKey === "0.0,0.0" ? 'location-icon-error' : 'location-icon-valid'} />
                                            </Tooltip>
                                        }
                                        {!result &&
                                            <GpsFixedIcon className={gettingCoordinates ? 'location-icon-animated' : 'location-icon'} />
                                        }
                                    </div>
                                } onClick={handleGetCoordinates}>
                                    {!onlyZipCodFieldHasData ? (
                                        <>
                                            Get Coordinates For Address
                                        </>
                                    ) :
                                        (
                                        <>
                                            Get Coordinates For Zip Code
                                        </>
                                        )
                                    }
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                }
                {!hasFetchGeoButton &&
                    <div className={'line'}></div>
                }

                <SelectLatitude initialValue={data?.geoFenceLatitude}
                    selectedLatitude={selectedLatitude}
                    setSelectedLatitude={setSelectedLatitude}
                    onChangeLatitude={() => setIsDirty(true)}
                    setError={setLatitudeError}
                    readOnly={readOnly}
                    required={allAddressFieldsRequired || allLocationFieldsRequired} />
                <SelectLongitude initialValue={data?.geoFenceLongitude}
                    selectedLongitude={selectedLongitude}
                    setSelectedLongitude={setSelectedLongitude}
                    onChangeLongitude={() => setIsDirty(true)}
                    setError={setLongitudeError}
                    readOnly={readOnly}
                    required={allAddressFieldsRequired || allLocationFieldsRequired} />

                <div className={'line'}></div>

                <SelectDistance initialValue={data?.geoFenceRadiusInMiles}
                    selectedDistance={selectedDistance}
                    setSelectedDistance={setSelectedDistance}
                    onChangeDistance={() => setIsDirty(true)}
                    readOnly={readOnly}
                    required={allAddressFieldsRequired || allLocationFieldsRequired} />

                {(!!selectedLatitude && !!selectedLongitude && !!selectedDistance) &&
                    <>
                        <div className={'button'}>
                            <Tooltip arrow title={showMap ? "Hide the embedded Google Map" : "Display embedded Google Map with pinned location"}>
                                <Button variant="outlined" endIcon={<MapIcon />} onClick={() => { setShowMap(!showMap) }}>
                                    {showMap ? 'Hide Map' : 'Show Map'}
                                </Button>
                            </Tooltip>
                        </div>
                        {showMap &&
                            <div className="gmap_canvas">
                                <iframe className={'iframe'} title="map" width="478" height="478" id="gmap_canvas" src={mapSrc} frameBorder={"0"} scrolling="no" marginHeight={0} marginWidth={0}>
                                </iframe>
                            </div>
                        }
                    </>
                }

            </div>


        </Fragment>
    );
};
